import React, { useState } from "react";
import { X } from "lucide-react";
import { toast } from "react-hot-toast";
import CustomSelect from "../../../components/ui/CustomSelect";
import { usePayoutMethods, usePayoutProviders, useCreatePayoutPartner } from "../hooks/usePayoutPartners";
import { useCurrencies } from "../../currency-settings/hooks/useCurrencies";

const emptyForm = {
  currencyKey: "",
  currencyCode: "",
  countryCode: "",
  payoutMethodUuid: "",
  providerUuid: "",
  isActive: false,
};

const currencyKey = (c) => `${c.currencyCode}|${c.countryCode}`;

const PayoutPartnerAddForm = ({ isOpen, onClose }) => {
  const [form, setForm] = useState(emptyForm);
  const { data: currenciesResponse } = useCurrencies();
  const currencies = currenciesResponse?.data || [];
  // A currencyCode like XOF or XAF spans several countries (each its own
  // Currency row) - the payout method list is scoped by country, so the
  // country has to be picked alongside the code, not guessed from it.
  const codeCounts = currencies.reduce((acc, c) => {
    acc[c.currencyCode] = (acc[c.currencyCode] || 0) + 1;
    return acc;
  }, {});
  const { data: payoutMethodsResponse } = usePayoutMethods(undefined, form.countryCode || undefined, {
    enabled: Boolean(form.countryCode),
  });
  const payoutMethods = payoutMethodsResponse?.data || [];
  const { data: providersResponse } = usePayoutProviders();
  const providers = providersResponse?.data || [];
  const { mutate: createPayoutPartner, isPending } = useCreatePayoutPartner();

  if (!isOpen) return null;

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleCurrencyChange = (key) => {
    const selected = currencies.find((c) => currencyKey(c) === key);
    setForm((prev) => ({
      ...prev,
      currencyKey: key,
      currencyCode: selected?.currencyCode || "",
      countryCode: selected?.countryCode || "",
      // The previously-selected method may not exist in the new country.
      payoutMethodUuid: "",
    }));
  };

  const handleClose = () => {
    setForm(emptyForm);
    onClose();
  };

  const handleSave = () => {
    if (!form.currencyCode || !form.payoutMethodUuid || !form.providerUuid) {
      toast.error("Currency, payout method, and provider are all required");
      return;
    }

    createPayoutPartner(
      {
        payoutMethodUuid: form.payoutMethodUuid,
        providerUuid: form.providerUuid,
        currencyCode: form.currencyCode,
        isActive: form.isActive,
      },
      {
        onSuccess: () => {
          toast.success("Provider linked to currency");
          handleClose();
        },
        onError: (err) => {
          toast.error(err?.response?.data?.message || "Failed to link provider");
        },
      }
    );
  };

  const currencyOptions = [
    { label: "Select a currency", value: "" },
    ...currencies.map((c) => ({
      value: currencyKey(c),
      label:
        codeCounts[c.currencyCode] > 1
          ? `${c.currencyCode.toUpperCase()} — ${c.country}`
          : c.currencyCode.toUpperCase(),
    })),
  ];
  const methodOptions = [
    { label: form.countryCode ? "Select a payout method" : "Select a currency first", value: "" },
    ...payoutMethods.map((m) => ({ value: m.uuid, label: m.name })),
  ];
  const providerOptions = [
    { label: "Select a provider", value: "" },
    ...providers.map((p) => ({ value: p.uuid, label: p.name })),
  ];

  return (
    <div className="flex fixed inset-0 z-50 justify-center items-center bg-black bg-opacity-50">
      <div className="overflow-y-auto mx-4 w-full max-w-lg max-h-[90vh] bg-white rounded-2xl shadow-2xl">
        <div className="flex justify-between items-center px-6 py-4 bg-gray-50 border-b">
          <h3 className="text-lg font-semibold text-gray-900">Add Provider</h3>
          <button onClick={handleClose} className="text-gray-400 hover:text-gray-600">
            <X size={20} />
          </button>
        </div>

        <div className="px-6 py-4 space-y-5">
          <div>
            <label className="block mb-1 text-xs font-bold tracking-wider text-gray-500 uppercase">
              Currency
            </label>
            <CustomSelect
              value={form.currencyKey}
              onChange={handleCurrencyChange}
              options={currencyOptions}
              placeholder="Select a currency"
            />
          </div>

          <div>
            <label className="block mb-1 text-xs font-bold tracking-wider text-gray-500 uppercase">
              Payout Method
            </label>
            <CustomSelect
              value={form.payoutMethodUuid}
              onChange={(value) => handleChange("payoutMethodUuid", value)}
              options={methodOptions}
              placeholder="Select a payout method"
            />
          </div>

          <div>
            <label className="block mb-1 text-xs font-bold tracking-wider text-gray-500 uppercase">
              Provider
            </label>
            <CustomSelect
              value={form.providerUuid}
              onChange={(value) => handleChange("providerUuid", value)}
              options={providerOptions}
              placeholder="Select a provider"
            />
          </div>

          <label className="flex gap-2 items-center text-sm text-gray-700 cursor-pointer">
            <input
              type="checkbox"
              checked={form.isActive}
              onChange={(e) => handleChange("isActive", e.target.checked)}
              className="w-4 h-4 rounded text-emerald-600 focus:ring-emerald-500"
            />
            Active
          </label>

          <p className="text-xs text-gray-400">
            Priority, fees, and payout limits can be set afterward by editing the new row -
            it's appended after every existing provider for this method and currency, so it
            won't be tried before ones already ranked.
          </p>
        </div>

        <div className="flex gap-3 justify-end px-6 py-4 bg-gray-50 border-t">
          <button
            onClick={handleClose}
            className="px-4 py-2 text-sm font-medium text-gray-600 rounded-lg hover:bg-gray-100"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={isPending}
            className="px-4 py-2 text-sm font-medium text-white bg-emerald-600 rounded-lg hover:bg-emerald-700 disabled:opacity-50"
          >
            {isPending ? "Adding..." : "Add provider"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PayoutPartnerAddForm;
